import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { useAuthStore } from '../auth/auth-store';
import { api } from './client';

/** Matches backend AdminUserView (modules/superadmin/services/users-admin.service.ts). */
export interface AdminUser {
  id: string;
  email: string;
  username: string;
  name: string;
  avatarUrl: string | null;
  isActive: boolean;
  isBanned: boolean;
  bannedAt: string | null;
  bannedReason: string | null;
  isSuperadmin: boolean;
  twoFactorEnabled: boolean;
  emailVerifiedAt: string | null;
  personalSubscriptionStatus: string;
  personalPlanCode: string | null;
  personalCurrentPeriodEnd: string | null;
  teamCount: number;
  createdAt: string;
  deletedAt: string | null;
}

export interface AdminUsersListResult {
  total: number;
  items: AdminUser[];
}

export interface AdminUsersQuery {
  limit?: number;
  offset?: number;
  search?: string;
  isBanned?: boolean;
  isSuperadmin?: boolean;
}

function buildQS(q: AdminUsersQuery): string {
  const sp = new URLSearchParams();
  if (q.limit !== undefined) sp.set('limit', String(q.limit));
  if (q.offset !== undefined) sp.set('offset', String(q.offset));
  if (q.search) sp.set('search', q.search);
  if (q.isBanned !== undefined) sp.set('isBanned', String(q.isBanned));
  if (q.isSuperadmin !== undefined) sp.set('isSuperadmin', String(q.isSuperadmin));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function adminUsersKey(query: AdminUsersQuery = {}): QueryKey {
  return ['admin', 'users', query];
}
export function adminUserKey(id: string): QueryKey {
  return ['admin', 'user', id];
}

export function useAdminUsers(query: AdminUsersQuery) {
  return useQuery({
    queryKey: adminUsersKey(query),
    queryFn: () => api<AdminUsersListResult>(`/api/admin/users${buildQS(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: id ? adminUserKey(id) : ['admin', 'user', 'noop'],
    queryFn: () => {
      if (!id) throw new Error('user id required');
      return api<AdminUser>(`/api/admin/users/${id}`);
    },
    enabled: Boolean(id),
  });
}

export interface UserTeamMembership {
  teamId: string;
  teamName: string;
  teamSlug: string;
  rolePreset: string | null;
  joinedAt: string;
  isActive: boolean;
}

export function useUserTeams(id: string | undefined) {
  return useQuery({
    queryKey: id ? ['admin', 'user', id, 'teams'] : ['admin', 'user', 'noop', 'teams'],
    queryFn: () => {
      if (!id) throw new Error('user id required');
      return api<UserTeamMembership[]>(`/api/admin/users/${id}/teams`);
    },
    enabled: Boolean(id),
  });
}

export function useBanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      api<AdminUser>(`/api/admin/users/${id}/ban`, { method: 'POST', body: { reason } }),
    onSuccess: (user) => {
      qc.setQueryData(adminUserKey(user.id), user);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUnbanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string | undefined }) =>
      api<AdminUser>(`/api/admin/users/${id}/unban`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
    onSuccess: (user) => {
      qc.setQueryData(adminUserKey(user.id), user);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string | undefined }) =>
      api<{ accepted: true }>(`/api/admin/users/${id}/reset-password`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
  });
}

export interface ImpersonateResult {
  sessionId: string;
  accessToken: string;
  expiresAt: string;
}

/**
 * Start an impersonation session.  On success swaps the admin's bearer
 * token for the new impersonation token — subsequent /api/* calls run as
 * the impersonated user (with `act` claim attribution).
 *
 * Note: this is one-way until /api/admin/users/:id/impersonate is called
 * with DELETE.  Frontend banner shows a "Stop impersonation" button that
 * triggers end + token swap back to the admin's prior access token.
 *
 * Phase 1 simplification: we don't preserve the admin's prior token —
 * after end, the operator must re-login.  Refinement deferred.
 */
export function useStartImpersonation() {
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      api<ImpersonateResult>(`/api/admin/users/${id}/impersonate`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (result) => {
      // Swap token.  The next /api/me will succeed under the impersonated
      // identity; admin endpoints will fail (impersonation_disallowed) until
      // the session ends.
      // Swap only the in-memory access token to the impersonated identity; the
      // admin's own refresh cookie stays put (ending impersonation re-logins).
      useAuthStore.getState().setTokens({
        accessToken: result.accessToken,
        accessExpiresAt: Date.parse(result.expiresAt),
      });
    },
  });
}

export function useEndImpersonation() {
  return useMutation({
    mutationFn: async ({ id }: { id: string }) =>
      api<void>(`/api/admin/users/${id}/impersonate`, { method: 'DELETE' }),
    onSuccess: () => {
      // Force re-login (cleanest path — current bearer is the impersonation
      // token which is now invalid).
      useAuthStore.getState().clear();
      window.location.href = '/login';
    },
  });
}

// ── account-state actions (verify / resend / deactivate / reactivate) ──────

function useUserAction(path: 'verify-email' | 'resend-verification' | 'deactivate' | 'reactivate') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string | undefined }) =>
      api<AdminUser | { accepted: true }>(`/api/admin/users/${id}/${path}`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
    onSuccess: (res, vars) => {
      if (res && 'id' in res) qc.setQueryData(adminUserKey(res.id), res);
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user', vars.id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export const useVerifyUserEmail = () => useUserAction('verify-email');
export const useResendVerification = () => useUserAction('resend-verification');
export const useDeactivateUser = () => useUserAction('deactivate');
export const useReactivateUser = () => useUserAction('reactivate');
